import { SoraneApi, type MessageRow, type ThreadRow } from './api.ts';
import { threadTitle } from './thread-title.ts';
import type { SoraneConfig } from './api.ts';

type View = 'auth' | 'main' | 'thread';
type Tab = 'new' | 'list';

function appendNodes(parent: Node, ...children: (Node | string)[]): void {
  for (const child of children) {
    parent.appendChild(child instanceof Node ? child : document.createTextNode(child));
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  appendNodes(node, ...children);
  return node;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function roleLabel(role: string): string {
  if (role === 'agent') return 'AI';
  if (role === 'admin') return '管理者';
  return '質問';
}

export class SoraneApp {
  private readonly api: SoraneApi;
  private view: View = 'auth';
  private tab: Tab = 'new';
  private threads: ThreadRow[] = [];
  private activeThread: ThreadRow | null = null;
  private messages: MessageRow[] = [];
  private busy = false;

  private get title(): string {
    return this.config.title ?? '空音';
  }

  private get lead(): string {
    return (
      this.config.lead ??
      '記事の URL と質問を投稿し、複数の AI が応える公開ディスカッションです。'
    );
  }

  private readonly root: HTMLElement;
  private readonly statusEl: HTMLElement;
  private readonly contentEl: HTMLElement;

  constructor(
    private readonly container: HTMLElement,
    private readonly config: SoraneConfig,
  ) {
    this.api = new SoraneApi(config);
    this.root = el('div', 'sorane');
    this.statusEl = el('div', 'sorane__status');
    this.contentEl = el('main');
    appendNodes(this.root, this.contentEl, this.statusEl);
    this.container.replaceChildren(this.root);
  }

  async mount(): Promise<void> {
    const session = await this.api.getSession();
    this.view = session?.role === 'inquirer' || session?.role === 'admin' ? 'main' : 'auth';
    await this.render();
    await this.openThreadFromQuery();
  }

  private async openThreadFromQuery(): Promise<void> {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get('ref')?.trim();
    const threadId = url.searchParams.get('thread')?.trim();
    const lookup = ref ?? threadId;
    if (!lookup || this.view === 'auth') return;
    await this.openThread(lookup);
  }

  private setThreadQuery(thread: ThreadRow): void {
    const url = new URL(window.location.href);
    url.searchParams.set('ref', thread.ref);
    url.searchParams.delete('thread');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  private clearThreadQuery(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    url.searchParams.delete('thread');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  private async render(): Promise<void> {
    this.contentEl.replaceChildren();
    this.setStatus('');

    if (this.view === 'auth') {
      appendNodes(this.contentEl, this.renderAuth());
      return;
    }
    if (this.view === 'thread' && this.activeThread) {
      appendNodes(this.contentEl, this.renderThread());
      return;
    }
    appendNodes(this.contentEl, this.renderMain());
  }

  private renderAuth(): HTMLElement {
    const panel = el('div', 'sorane__panel sorane__stack');
    appendNodes(
      panel,
      el('h1', 'sorane__title', [this.title]),
      el('p', 'sorane__lead', [`${this.lead} Passkey で本人確認を行います。`]),
    );

    const row = el('div', 'sorane__stack');
    const registerBtn = el('button', 'sorane__btn', ['Passkey を登録して始める']);
    registerBtn.addEventListener('click', () => void this.handleRegister());
    const loginBtn = el('button', 'sorane__btn sorane__btn--text', ['Passkey でログイン']);
    loginBtn.addEventListener('click', () => void this.handleLogin());
    appendNodes(row, registerBtn, loginBtn);
    appendNodes(panel, row);
    return panel;
  }

  private renderMain(): HTMLElement {
    const wrap = el('div', 'sorane__stack');
    appendNodes(
      wrap,
      el('h1', 'sorane__title', [this.title]),
      el('p', 'sorane__lead', [this.lead]),
    );

    const tabs = el('div', 'sorane__stack');
    const newTab = el(
      'button',
      `sorane__btn${this.tab === 'new' ? '' : ' sorane__btn--text'}`,
      ['新しい問い'],
    );
    newTab.addEventListener('click', () => {
      this.tab = 'new';
      void this.render();
    });
    const listTab = el(
      'button',
      `sorane__btn${this.tab === 'list' ? '' : ' sorane__btn--text'}`,
      ['一覧'],
    );
    listTab.addEventListener('click', () => {
      this.tab = 'list';
      void this.loadThreads();
    });
    appendNodes(tabs, newTab, listTab);
    appendNodes(wrap, tabs);

    if (this.tab === 'new') {
      appendNodes(wrap, this.renderSubmitForm());
    } else {
      appendNodes(wrap, this.renderThreadList());
    }

    const logoutBtn = el('button', 'sorane__btn sorane__btn--text', ['ログアウト']);
    logoutBtn.addEventListener('click', () => void this.handleLogout());
    appendNodes(wrap, logoutBtn);

    return wrap;
  }

  private renderSubmitForm(): HTMLElement {
    const panel = el('div', 'sorane__panel sorane__stack');
    const form = el('form', 'sorane__stack');

    const urlLabel = el('label', 'sorane__stack');
    appendNodes(urlLabel, el('span', '', ['記事 URL']), el('span', 'sorane__meta', [' *必須']));
    const urlInput = el('input', 'sorane__input') as HTMLInputElement;
    urlInput.type = 'url';
    urlInput.placeholder = 'https://example.com/article';
    urlInput.required = true;
    appendNodes(urlLabel, urlInput);

    const qLabel = el('label', 'sorane__stack');
    appendNodes(qLabel, el('span', '', ['質問']), el('span', 'sorane__meta', [' *必須']));
    const qInput = el('textarea', 'sorane__textarea') as HTMLTextAreaElement;
    qInput.placeholder = 'この記事について、何を知りたいですか？';
    qInput.required = true;
    qInput.maxLength = 2000;
    appendNodes(qLabel, qInput);

    const submit = el('button', 'sorane__btn', ['投稿する']);
    submit.type = 'submit';
    appendNodes(form, urlLabel, qLabel, submit);
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void this.handleSubmit(urlInput.value, qInput.value);
    });
    appendNodes(panel, form);
    return panel;
  }

  private renderThreadList(): HTMLElement {
    const panel = el('div', 'sorane__panel');
    if (this.threads.length === 0) {
      appendNodes(panel, el('p', 'sorane__meta', ['まだ問いはありません。']));
      return panel;
    }

    const list = el('ul', 'sorane__stack');
    for (const thread of this.threads) {
      const item = el('li');
      const link = el('button', 'sorane__link', [threadTitle(thread)]);
      link.type = 'button';
      link.addEventListener('click', () => void this.openThread(thread.ref));
      appendNodes(
        item,
        link,
        el('div', 'sorane__meta', [formatDate(thread.updatedAt)]),
      );
      appendNodes(list, item);
    }
    appendNodes(panel, list);
    return panel;
  }

  private renderThread(): HTMLElement {
    const thread = this.activeThread!;
    const wrap = el('div', 'sorane__stack');

    const back = el('button', 'sorane__btn sorane__btn--text', ['← 一覧に戻る']);
    back.addEventListener('click', () => {
      this.view = 'main';
      this.tab = 'list';
      this.activeThread = null;
      this.clearThreadQuery();
      void this.loadThreads();
    });
    appendNodes(wrap, back);

    appendNodes(wrap, el('h2', 'sorane__title', [threadTitle(thread)]));

    if (thread.canonicalUrl) {
      const link = el('a', 'sorane__link', [thread.urlTitle ?? thread.canonicalUrl]) as HTMLAnchorElement;
      link.href = thread.canonicalUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      appendNodes(wrap, el('p', 'sorane__meta', ['参照: ', link]));
    }

    if (thread.subject) {
      appendNodes(wrap, el('p', '', [thread.subject]));
    }

    const messagesEl = el('div');
    for (const msg of this.messages) {
      const cls =
        msg.authorRole === 'agent'
          ? 'sorane__message sorane__message--agent'
          : 'sorane__message';
      const box = el('div', cls);
      appendNodes(
        box,
        el('div', 'sorane__meta', [`${roleLabel(msg.authorRole)} · ${formatDate(msg.createdAt)}`]),
        el('div', '', [msg.body]),
      );
      appendNodes(messagesEl, box);
    }
    appendNodes(wrap, messagesEl);

    if (this.messages.filter((m) => m.authorRole === 'agent').length === 0) {
      appendNodes(
        wrap,
        el('p', 'sorane__meta', ['AI の応答を待っています。']),
      );
    }

    return wrap;
  }

  private async handleRegister(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus('Passkey を準備しています…');
    try {
      await this.api.register();
      this.view = 'main';
      this.setStatus('登録しました', 'ok');
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : '登録に失敗しました', 'error');
    } finally {
      this.busy = false;
    }
  }

  private async handleLogin(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus('Passkey を確認しています…');
    try {
      await this.api.login();
      this.view = 'main';
      this.setStatus('ログインしました', 'ok');
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : 'ログインに失敗しました', 'error');
    } finally {
      this.busy = false;
    }
  }

  private async handleLogout(): Promise<void> {
    this.view = 'auth';
    this.activeThread = null;
    this.clearThreadQuery();
    await this.render();
    this.setStatus('ログアウトしました（ブラウザのセッションは API 側で失効します）', 'ok');
  }

  private async handleSubmit(url: string, question: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus('投稿しています…');
    try {
      await this.api.createThread(url.trim(), question.trim());
      this.setStatus('投稿しました', 'ok');
      this.tab = 'list';
      await this.loadThreads();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : '投稿に失敗しました', 'error');
    } finally {
      this.busy = false;
    }
  }

  private async loadThreads(): Promise<void> {
    this.view = 'main';
    this.activeThread = null;
    this.setStatus('読み込み中…');
    try {
      this.threads = await this.api.listThreads();
      this.setStatus('');
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : '一覧の取得に失敗しました', 'error');
    }
  }

  private async openThread(lookup: string): Promise<void> {
    this.setStatus('読み込み中…');
    try {
      const res = await this.api.getThread(lookup);
      this.activeThread = res.thread;
      this.messages = res.messages;
      this.view = 'thread';
      this.setThreadQuery(res.thread);
      this.setStatus('');
      await this.render();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : 'スレッドの取得に失敗しました', 'error');
    }
  }

  private setStatus(message: string, kind?: 'error' | 'ok'): void {
    this.statusEl.textContent = message;
    this.statusEl.className = 'sorane__status';
    if (kind === 'error') this.statusEl.classList.add('sorane__status--error');
    if (!message) this.statusEl.className = 'sorane__status';
  }
}